import type { ReactNode } from 'react';
import { cx } from '../cx';
import { WIDE_ONLY, type ChartLayout } from './layout';
import { spreadLabels } from './LineChart';
import { LABEL_TEXT, VALUE_TEXT, chartStroke, niceScale, percentOf } from './scale';

export type SlopeRow = {
  id: string;
  label: ReactNode;
  a: number;
  b: number;
  displayA: ReactNode;
  displayB: ReactNode;
};

export type SlopeChartProps = {
  rows: SlopeRow[];
  /** The two moments ("Oct 2025", "Oct 2026"). */
  columns: [ReactNode, ReactNode];
  /** The story line: ink with a sun end marker; the rest context. */
  highlightId?: string;
  scale?: { min: number; max: number };
  /** `auto` (default) switches to the narrow form in a narrow container: the left labels show values only (names are written once, on the right) and the side gutters are narrower. */
  layout?: ChartLayout;
  /** One sentence that states the finding and the key values, for screen readers. */
  description: string;
  className?: string;
};

// In the narrow form the left side holds values only, so the right side, which carries the names, gets the room.
const LEFT: Record<ChartLayout, string> = { auto: 'w-1/6 @md:w-1/3', narrow: 'w-1/6', wide: 'w-1/3' };
const RIGHT: Record<ChartLayout, string> = { auto: 'w-2/5 @md:w-1/3', narrow: 'w-2/5', wide: 'w-1/3' };

/** Two moments joined by one line per category: what changed, and who overtook whom. */
export function SlopeChart({
  rows,
  columns,
  highlightId,
  scale: fixed,
  layout = 'auto',
  description,
  className,
}: SlopeChartProps) {
  const values = rows.flatMap((r) => [r.a, r.b]);
  const scale = fixed ?? niceScale(Math.min(...values), Math.max(...values));
  const top = (v: number) => 100 - percentOf(v, scale.min, scale.max);
  const leftY = spreadLabels(
    rows.map((r) => top(r.a)),
    9,
  );
  const rightY = spreadLabels(
    rows.map((r) => top(r.b)),
    9,
  );
  const order = [...rows.filter((r) => r.id !== highlightId), ...rows.filter((r) => r.id === highlightId)];
  return (
    <div role="img" aria-label={description} className={cx('@container flex flex-col gap-2', className)}>
      <div className="flex text-xs font-semibold text-jc-ink-secondary" aria-hidden="true">
        <span className={cx('pr-3 text-right', LEFT[layout])}>{columns[0]}</span>
        <span className="flex-1" />
        <span className={cx('pl-3', RIGHT[layout])}>{columns[1]}</span>
      </div>
      <div className="flex h-52" aria-hidden="true">
        <div className={cx('relative', LEFT[layout])}>
          {rows.map((r, i) => (
            <span
              key={r.id}
              className={cx(
                'absolute right-3 -translate-y-1/2 truncate text-right',
                LABEL_TEXT,
                r.id === highlightId && 'font-semibold text-jc-ink',
              )}
              style={{ top: `${leftY[i]}%`, maxWidth: 'calc(100% - 0.75rem)' }}
            >
              <span className={WIDE_ONLY[layout]}>{r.label} </span>
              <span className={VALUE_TEXT}>{r.displayA}</span>
            </span>
          ))}
        </div>
        <div className="relative flex-1 border-x border-jc-chart-grid">
          <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="absolute inset-0 size-full overflow-visible">
            {order.map((r) => (
              <line
                key={r.id}
                x1="0"
                x2="100"
                y1={top(r.a)}
                y2={top(r.b)}
                vectorEffect="non-scaling-stroke"
                strokeWidth={r.id === highlightId ? 3 : 2}
                className={chartStroke(r.id === highlightId ? 'subject' : 'context')}
              />
            ))}
          </svg>
          {order.map((r) => (
            <span key={r.id}>
              <span
                className={cx(
                  'absolute left-0 size-2 -translate-1/2 rounded-full',
                  r.id === highlightId ? 'bg-jc-ink' : 'bg-jc-chart-context',
                )}
                style={{ top: `${top(r.a)}%` }}
              />
              <span
                className={cx(
                  'absolute left-full -translate-1/2 rounded-full',
                  r.id === highlightId ? 'size-3 bg-jc-chart-2 ring-2 ring-jc-ink' : 'size-2 bg-jc-chart-context',
                )}
                style={{ top: `${top(r.b)}%` }}
              />
            </span>
          ))}
        </div>
        <div className={cx('relative', RIGHT[layout])}>
          {rows.map((r, i) => (
            <span
              key={r.id}
              className={cx(
                'absolute left-3 -translate-y-1/2 truncate',
                LABEL_TEXT,
                r.id === highlightId && 'font-semibold text-jc-ink',
              )}
              style={{ top: `${rightY[i]}%`, maxWidth: 'calc(100% - 0.75rem)' }}
            >
              <span className={VALUE_TEXT}>{r.displayB}</span> {r.label}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}
